import { useState, useEffect, useMemo, useCallback } from "react";
import { ResponsiveLineCanvas } from "@nivo/line";
import { useTheme, Box, Typography, CircularProgress, useMediaQuery, ButtonGroup, Button, Alert, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { tokens } from "../../theme";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const SpsDistributionHistoryChart = ({ days = 30, initialViewMode, onViewModeChange, hideRewards = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState(days);
  const [selectedCategories, setSelectedCategories] = useState(['burned', 'staked', 'unstaking', 'liquidOnchain', 'liquidOffchain', 'daoReserveOnchain', 'daoOffchain', 'rewardPools']);
  const [selectedRewards, setSelectedRewards] = useState([]);
  const [viewMode, setViewMode] = useState(hideRewards ? 'distribution' : (initialViewMode || 'distribution')); // 'distribution' oder 'rewardPools'
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Available timeframes
  const timeframes = [
    { label: '7D', value: 7 },
    { label: '30D', value: 30 },
    { label: '90D', value: 90 },
    { label: '1Y', value: 365 }
  ];

  // Category information with colors from Nivo's default scheme - wrapped in useMemo to fix ESLint warning
  // These colors match the ones used by the SpsDistributionChart component
  const categoryInfo = useMemo(() => ({
    burned: {
      label: "Burned",
      description: "SPS tokens permanently removed from circulation",
      color: "#e8c1a0"
    },
    staked: {
      label: "Staked",
      description: "SPS tokens staked by users",
      color: "#f47560"
    },
    unstaking: {
      label: "Unstaking",
      description: "SPS tokens in the process of being unstaked",
      color: "#ffd93d"
    },
    liquidOnchain: {
      label: "Player Liquid (On-chain)",
      description: "Player SPS tokens on Hive blockchain (not staked)",
      color: "#ff9f40"
    },
    liquidOffchain: {
      label: "Player Liquid (Off-chain)",
      description: "Player SPS on other blockchains (BSC, Base, ETH, Hive-Engine)",
      color: "#4ecdc4"
    },
    daoReserveOnchain: {
      label: "DAO Reserve (On-chain)",
      description: "DAO Reserve on Hive blockchain",
      color: "#95e1d3"
    },
    daoOffchain: {
      label: "DAO Off-chain",
      description: "DAO SPS on other blockchains (in DEX pools)",
      color: "#f9ca24"
    },
    rewardPools: {
      label: "Reward Pools",
      description: "SPS tokens allocated to various reward programs",
      color: "#c7a17a"
    }
  }), []);

  // Short names for common reward pools (from SpsDistributionChart.jsx)
  const shortNameMap = useMemo(() => ({
    "$UNCLAIMED_UNISWAP_REWARDS": "Uniswap",
    "$REWARD_POOLS_LAND": "Land",
    "$REWARD_POOLS_BRAWL": "Brawl",
    "$SPS_STAKING_REWARDS": "Staking",
    "$REWARD_POOLS_LICENSE": "License",
    "$TOURNAMENTS_DISTRIBUTION": "Tournaments",
    "$VALIDATOR_REWARDS": "Validators",
    "$REWARD_POOLS_SOULKEEP": "Soulkeep", 
    "$REWARD_POOLS_MODERN": "Modern",
    "$REWARD_POOLS_WILD": "Wild",
    "$REWARD_POOLS_SURVIVAL": "Survival",
    "$REWARD_POOLS_FOCUS": "Focus",
    "$REWARD_POOLS_SEASON": "Season"
  }), []);

  // Generate a consistent color for each reward type
  const getColorForReward = useCallback((rewardKey, index) => {
    // Base colors - a nice palette
    const baseColors = [
      "#e8c1a0", // Sand
      "#f47560", // Coral
      "#61cdbb", // Teal
      "#f1e15b", // Yellow
      "#e8a838", // Amber
      "#97e3d5", // Mint
      "#e25c3b", // Rust
      "#7b6888", // Purple
      "#a05195", // Magenta
      "#d45087", // Pink
      "#ff7c43", // Orange
      "#8dc5f3", // Sky Blue
      "#3f8a9c", // Teal Blue
      "#59c067", // Green
      "#ffa600"  // Gold
    ];

    // For known keys, use a consistent color from the palette
    const knownKeys = Object.values(shortNameMap);
    const keyIndex = knownKeys.indexOf(rewardKey);
    
    if (keyIndex !== -1 && keyIndex < baseColors.length) {
      return baseColors[keyIndex];
    }
    
    // For other keys, generate a color based on the index
    return baseColors[index % baseColors.length];
  }, [shortNameMap]);

  // Format numbers in a user-friendly way
  const formatNumber = useCallback((value) => {
    // Format to millions with 1 decimal place
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return Math.round(value * 100) / 100;
  }, []);

  // Format percentage values
  const formatPercent = useCallback((value) => {
    return `${parseFloat(value).toFixed(1)}%`;
  }, []);

  // Change timeframe
  const handleTimeframeChange = useCallback((value) => {
    setSelectedTimeframe(value);
  }, []);


  // Fetch trends data separately if needed
  const fetchTrendsData = useCallback(async () => {
    if (viewMode !== 'distribution' || selectedTimeframe <= 90) return null;
    
    try {
      // Bestimme die passende Aggregation basierend auf dem Zeitraum
      let aggregateBy = 'day';
      if (selectedTimeframe > 365) {
        aggregateBy = 'month';
      } else if (selectedTimeframe > 90) {
        aggregateBy = 'week';
      }
      
      const response = await fetch(
        `https://api.elmo-burns.com/api/v1/sps-distribution-history/trends/?period=${selectedTimeframe}&aggregate_by=${aggregateBy}&nocache=${Date.now()}`
      );
      
      if (!response.ok) return null;
      
      const trendsData = await response.json();
      return trendsData;
    } catch (error) {
      console.error("Error fetching trends data:", error);
      return null;
    }
  }, [viewMode, selectedTimeframe]);

  // Toggle category selection
  const toggleCategory = useCallback((category) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        // Don't allow deselecting all categories
        if (prev.length === 1) return prev;
        return prev.filter(c => c !== category);
      } else {
        return [...prev, category];
      }
    });
  }, []);

  // Toggle reward selection
  const toggleReward = useCallback((reward) => {
    setSelectedRewards(prev => {
      if (prev.includes(reward)) {
        // Don't allow deselecting all rewards
        if (prev.length === 1) return prev;
        return prev.filter(r => r !== reward);
      } else {
        return [...prev, reward];
      }
    });
  }, []);

  // Change view mode
  const handleViewModeChange = useCallback((event, newMode) => {
    if (newMode !== null && !hideRewards) {
      setViewMode(newMode);
      // Inform parent component about the change so it can sync with other charts
      if (onViewModeChange) {
        onViewModeChange(newMode);
      }
      
      // Bei Wechsel zum Reward Pools Modus lädt die Daten neu,
      // da möglicherweise ein anderer API-Endpunkt verwendet wird
      if (newMode === 'rewardPools' && selectedTimeframe > 90) {
        // Setze timeframe neu, um die useEffect Hook zum Laden der Daten zu triggern
        setSelectedTimeframe(prevTimeframe => prevTimeframe);
      }
    }
  }, [selectedTimeframe, onViewModeChange, hideRewards]);

  // Process raw data into chart format
  const processChartData = useCallback((historyData, timeframe) => {
    // Sort chronologically (oldest to newest)
    const sortedData = [...historyData].sort((a, b) => 
      new Date(a.timestamp) - new Date(b.timestamp)
    );
    
    // Limit to the selected timeframe
    const limitedData = sortedData.slice(-timeframe);

    // Je nach viewMode unterschiedliche Daten verarbeiten
    if (viewMode === 'distribution') {
      // Initialize series for each category
      const series = {
        burned: { id: 'burned', data: [] },
        staked: { id: 'staked', data: [] },
        unstaking: { id: 'unstaking', data: [] },
        liquidOnchain: { id: 'liquidOnchain', data: [] },
        liquidOffchain: { id: 'liquidOffchain', data: [] },
        daoReserveOnchain: { id: 'daoReserveOnchain', data: [] },
        daoOffchain: { id: 'daoOffchain', data: [] },
        rewardPools: { id: 'rewardPools', data: [] }
      };
      
      // Populate series with data points
      limitedData.forEach(day => {
        const date = day.date;
        
        Object.entries(day.distribution).forEach(([key, data]) => {
          if (series[key]) {
            series[key].data.push({
              x: date,
              y: parseFloat(data.percentage),
              rawValue: data.value // Store raw value for tooltip
            });
          }
        });
      });
      
      // Format the data for the chart and add colors/metadata
      return Object.values(series).map(serie => ({
        ...serie,
        color: categoryInfo[serie.id].color,
        label: categoryInfo[serie.id].label
      }));
    } else {
      // Für reward pools, erst alle Reward-Typen über alle Tage sammeln
      const allRewardTypes = new Set();
      limitedData.forEach(day => {
        if (day.rewardPoolsDetail) {
          Object.keys(day.rewardPoolsDetail).forEach(key => {
            allRewardTypes.add(key);
          });
        }
      });

      // Initialize series for each reward type
      const series = {};
      Array.from(allRewardTypes).forEach(rewardType => {
        series[rewardType] = { id: rewardType, data: [] };
      });
      
      // Populate series with data points
      limitedData.forEach(day => {
        const date = day.date;
        
        // For each reward type, add data point for this day
        Array.from(allRewardTypes).forEach(rewardType => {
          const value = day.rewardPoolsDetail?.[rewardType]?.value || 0;
          const percentage = day.rewardPoolsDetail?.[rewardType]?.percentage || "0";

          series[rewardType].data.push({
            x: date,
            y: parseFloat(percentage),
            rawValue: value // Store raw value for tooltip
          });
        });
      });
      
      // Format the data for the chart and add colors/metadata
      // Sort by average percentage value (highest first)
      return Object.values(series)
        .map((serie, index) => {
          // Calculate average percentage (excluding zeros)
          const nonZeroValues = serie.data.filter(d => d.y > 0);
          const avgPercentage = nonZeroValues.length > 0 ? 
            nonZeroValues.reduce((sum, d) => sum + d.y, 0) / nonZeroValues.length : 0;
          
          return {
            ...serie,
            avgPercentage,
            color: getColorForReward(serie.id, index)
          };
        })
        .sort((a, b) => b.avgPercentage - a.avgPercentage);
    }
  }, [categoryInfo, getColorForReward, viewMode]);

  // Fetch data for the chart
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Intelligente API-Auswahl basierend auf aktueller Ansicht und Zeitraum
        let apiUrl;
        let requestedMetrics = '';
        
        // Bei Reward Pools-Ansicht speziell die benötigten Daten anfordern
        if (viewMode === 'rewardPools') {
          requestedMetrics = 'date,reward_pools';
        }
        
        // Auswahl der optimalen API-Route basierend auf Zeitraum und Ansicht
        if (selectedTimeframe > 365) {
          // Für sehr lange Zeiträume (>1 Jahr) - höchste Optimierung
          const resolution = 'monthly';
          apiUrl = `https://api.elmo-burns.com/api/v1/sps-distribution-history/chart-data/?period=${selectedTimeframe}&resolution=${resolution}${requestedMetrics ? '&metrics=' + requestedMetrics : ''}&nocache=${Date.now()}`;
        } 
        else if (selectedTimeframe > 90) {
          // Für mittlere Zeiträume (3 Monate - 1 Jahr)
          const resolution = 'weekly';
          apiUrl = `https://api.elmo-burns.com/api/v1/sps-distribution-history/chart-data/?period=${selectedTimeframe}&resolution=${resolution}${requestedMetrics ? '&metrics=' + requestedMetrics : ''}&nocache=${Date.now()}`;
        }
        else {
          // Für kurze Zeiträume - Standard API mit Filterung
          apiUrl = `https://api.elmo-burns.com/api/v1/sps-distribution-history/?period=${selectedTimeframe}${requestedMetrics ? '&metrics=' + requestedMetrics : ''}&nocache=${Date.now()}`;
        }
        
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          throw new Error(`Error fetching data: ${response.status} ${response.statusText}`);
        }
        
        const apiResponse = await response.json();
        
        if (!apiResponse || !apiResponse.results || !Array.isArray(apiResponse.results)) {
          throw new Error('Invalid API response format');
        }
        
        // Transform API data to match the expected format for chart processing
        const historyData = apiResponse.results.map(item => {
          // Calculate on-chain values (reserve.total and liquid contain off-chain, need to subtract)
          const offchain_total = item.offchain_total || 0;
          const player_offchain = item.player_offchain_total || 0;
          const dao_offchain = item.dao_offchain_total || 0;
          
          // Reserve on-chain = reserve.total - off_chain.total
          const reserve_onchain = item.reserve_value - offchain_total;
          
          // Liquid on-chain is already correct in liquid_value (residual calculation)
          const liquid_onchain = item.liquid_value;
          
          // Create distribution object structure with new off-chain categories
          const distribution = {
            burned: {
              value: item.burned_value,
              percentage: item.burned_percentage 
            },
            staked: {
              value: item.staked_value,
              percentage: item.staked_percentage
            },
            unstaking: {
              value: item.unstaking_value,
              percentage: item.unstaking_percentage
            },
            liquidOnchain: {
              value: liquid_onchain,
              percentage: item.liquid_percentage // Already correct percentage for on-chain only
            },
            liquidOffchain: {
              value: player_offchain,
              percentage: item.total_minted ? (player_offchain / item.total_minted * 100) : 0
            },
            daoReserveOnchain: {
              value: reserve_onchain,
              percentage: item.total_minted ? (reserve_onchain / item.total_minted * 100) : 0
            },
            daoOffchain: {
              value: dao_offchain,
              percentage: item.total_minted ? (dao_offchain / item.total_minted * 100) : 0
            },
            rewardPools: {
              value: item.reward_pools_value,
              percentage: item.reward_pools_percentage
            }
          };
          
          // Create rewardPoolsDetail structure from nested reward_pools data
          const rewardPoolsDetail = {};
          if (item.reward_pools && Array.isArray(item.reward_pools)) {
            item.reward_pools.forEach(pool => {
              rewardPoolsDetail[pool.pool_name] = {
                originalId: pool.original_id,
                value: pool.value,
                percentage: pool.percentage
              };
            });
          }
          
          return {
            timestamp: item.timestamp,
            date: item.date,
            distribution,
            rewardPoolsDetail,
            totals: {
              minted: item.total_minted,
              totalSupply: item.total_supply
            }
          };
        });
        
        // Process the transformed data
        const processedData = processChartData(historyData, selectedTimeframe);
        setChartData(processedData);

        // Set initial selection based on viewMode
        if (viewMode === 'rewardPools' && selectedRewards.length === 0) {
          // For Reward Pools: Select top 5 rewards
          const top5Rewards = processedData.slice(0, 5).map(serie => serie.id);
          setSelectedRewards(top5Rewards);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error loading distribution history from API:', err);
        setError(`Failed to load distribution history: ${err.message}`);
        setChartData([]);
      }      finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    // Setup a refresh interval (every 30 minutes)
    const intervalId = setInterval(() => {
      fetchData();
    }, 30 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, [selectedTimeframe, processChartData, selectedRewards.length, viewMode]);

  // Filter chart data based on selected categories/rewards and current view mode
  const filteredChartData = useMemo(() => {
    if (viewMode === 'distribution') {
      return chartData.filter(serie => selectedCategories.includes(serie.id));
    } else {
      return chartData.filter(serie => selectedRewards.includes(serie.id));
    }
  }, [chartData, selectedCategories, selectedRewards, viewMode]);
  
  // Calculate optimal Y-axis range based on the filtered data
  const yAxisRange = useMemo(() => {
    const values = filteredChartData.flatMap(serie => 
      serie.data.map(d => parseFloat(d.y))
    ).filter(v => !isNaN(v));
    if (values.length === 0) {
      return { 
        min: 0, 
        max: viewMode === 'distribution' ? 40 : 100,
        isZoomed: false
      };
    }
    const maxValue = Math.max(...values);
    const minValue = Math.min(...values.filter(v => v > 0));
    const valueRange = maxValue - minValue;
    // Zoom: Wenn 2 oder 3 Stats ausgewählt und der Wertebereich eng ist, Y-Achse dynamisch anpassen
    const shouldZoom = (filteredChartData.length <= 3 && valueRange < 10 && minValue > 2) || (filteredChartData.length === 2 && valueRange < 15);
    if (shouldZoom) {
      // Zoom-Bereich: min etwas unter den kleinsten Wert, max etwas über den größten Wert
      const zoomedMin = Math.max(0, Math.floor(minValue - valueRange * 0.2));
      const zoomedMax = Math.ceil(maxValue + valueRange * 0.2);
      return { min: zoomedMin, max: zoomedMax, isZoomed: true };
    }
    // Standard: 0 bis max mit Buffer
    const bufferFactor = maxValue < 10 ? 1.2 : maxValue < 50 ? 1.15 : 1.1;
    return { 
      min: 0,
      max: maxValue * bufferFactor,
      isZoomed: false
    };
  }, [filteredChartData, viewMode]);

  // Customize the chart theme
  const chartTheme = useMemo(() => ({
    axis: {
      domain: {
        line: {
          stroke: colors.grey[100],
          strokeWidth: 1
        },
      },
      ticks: {
        line: {
          stroke: colors.grey[100],
          strokeWidth: 1
        },
        text: {
          fill: colors.grey[100],
          fontSize: 11
        }
      },
      legend: {
        text: {
          fill: colors.grey[100],
          fontSize: 12,
          fontWeight: 'bold'
        }
      }
    },
    grid: {
      line: {
        stroke: colors.grey[800],
        strokeWidth: 1
      }
    },
    legends: {
      text: {
        fill: colors.grey[100],
        fontSize: 11
      }
    },
    tooltip: {
      container: {
        background: colors.primary[400],
        color: colors.grey[100]
      }
    },
    crosshair: {
      line: {
        stroke: colors.grey[200],
        strokeWidth: 1,
        strokeOpacity: 0.75
      }
    },
    annotations: {
      text: {
        fontSize: 13,
        fill: colors.grey[100],
        outlineWidth: 2,
        outlineColor: '#ffffff',
        outlineOpacity: 0.2
      }
    }
  }), [colors]);

  // Render chart
  return (
    <Box 
      height="100%" 
      width="100%"
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
        position: 'relative',
      }}
    >
      {/* Header row with title and controls */}
      <Box 
        display="flex" 
        flexDirection={isMobile ? "column" : "row"} 
        alignItems={isMobile ? "flex-start" : "center"} 
        justifyContent="space-between" 
        mb={2}
      >
        <Box display="flex" alignItems="center" mb={isMobile ? 1 : 0}>
          <Typography variant="h5" fontWeight="bold" mr={1}>
            SPS {viewMode === 'distribution' ? 'DISTRIBUTION' : 'REWARD POOLS'} HISTORY
          </Typography>
        </Box>
        
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          {/* View Mode Switcher - only show if rewards are not hidden */}
          {!hideRewards && (
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              aria-label="Chart View Mode"
              size="small"
              color="secondary"
              sx={{ mr: 1 }}
            >
              <ToggleButton value="distribution" aria-label="Distribution">
                Distribution
              </ToggleButton>
              <ToggleButton value="rewardPools" aria-label="Reward Pools">
                Rewards
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        
          {/* Time Frame Buttons */}
          <ButtonGroup 
            size="small" 
            variant="outlined" 
            aria-label="timeframe selection"
            sx={{ 
              '.MuiButtonGroup-grouped': {
                borderColor: colors.grey[700],
              }
            }}
          >
            {timeframes.map((timeframe) => (
              <Button
                key={timeframe.value}
                onClick={() => handleTimeframeChange(timeframe.value)}
                sx={{
                  color: selectedTimeframe === timeframe.value ? colors.greenAccent[400] : colors.grey[300],
                  borderColor: colors.grey[700],
                  backgroundColor: selectedTimeframe === timeframe.value ? colors.primary[600] : 'transparent',
                  '&:hover': {
                    backgroundColor: colors.primary[600],
                    borderColor: colors.greenAccent[400],
                  },
                  minWidth: isMobile ? 35 : 45,
                  px: isMobile ? 1 : 2
                }}
              >
                {timeframe.label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
      </Box>
      
      {/* Category toggle buttons - basierend auf dem aktuellen View Mode - OHNE SCROLLBAR */}
      <Box 
        display="flex" 
        flexWrap="wrap" 
        gap={1} 
        mb={4}
        mt={2}
        sx={{
          position: 'relative',
          zIndex: 5,
          minHeight: '50px',
          pb: 1.5,
          pt: 1,
          px: 1,
          borderRadius: '4px',
        }}
      >
        {viewMode === 'distribution' ? (
          // Distribution Categories
          Object.entries(categoryInfo).map(([key, info]) => (
            <Button
              key={key}
              variant={selectedCategories.includes(key) ? "contained" : "outlined"}
              size="small"
              onClick={() => toggleCategory(key)}
              sx={{
                backgroundColor: selectedCategories.includes(key) ? info.color : 'transparent',
                color: selectedCategories.includes(key) ? '#000' : info.color,
                borderColor: info.color,
                '&:hover': {
                  backgroundColor: selectedCategories.includes(key) 
                    ? info.color
                    : `${info.color}22`,
                  borderColor: info.color,
                  boxShadow: `0 0 5px ${info.color}`
                },
                '&:active': {
                  boxShadow: selectedCategories.includes(key) ? `0 0 8px ${info.color}` : 'none',
                },
                textTransform: 'none',
                fontWeight: 'bold',
                minWidth: 'auto',
                px: 1.5
              }}
            >
              {info.label}
            </Button>
          ))
        ) : (
          // Reward Pools - Nur anzeigen, wenn rewardPoolsDetail-Daten vorhanden sind
          chartData.map((serie) => (
            <Button
              key={serie.id}
              variant={selectedRewards.includes(serie.id) ? "contained" : "outlined"}
              size="small"
              onClick={() => toggleReward(serie.id)}
              sx={{
                backgroundColor: selectedRewards.includes(serie.id) ? serie.color : 'transparent',
                color: selectedRewards.includes(serie.id) ? '#000' : serie.color,
                borderColor: serie.color,
                '&:hover': {
                  backgroundColor: selectedRewards.includes(serie.id) 
                    ? serie.color
                    : `${serie.color}22`,
                  borderColor: serie.color,
                  boxShadow: `0 0 5px ${serie.color}`
                },
                '&:active': {
                  boxShadow: selectedRewards.includes(serie.id) ? `0 0 8px ${serie.color}` : 'none',
                },
                textTransform: 'none',
                fontWeight: 'bold',
                minWidth: 'auto',
                px: 1.5,
                marginBottom: '4px',
              }}
            >
              {serie.id}
            </Button>
          ))
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="300px">
          <CircularProgress />
        </Box>
      ) : filteredChartData.length > 0 && filteredChartData[0].data.length > 0 ? (
        <Box 
          sx={{ 
            height: isMobile ? "350px" : "380px",
            mt: 4,
            mb: 2,
            position: 'relative',
            width: '100%',
            zIndex: 1,
            overflow: 'visible',
            flex: 1
          }}
        >
          {/* Zoomed View Indicator */}
          {yAxisRange.isZoomed && (
            <Box 
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                backgroundColor: colors.primary[400],
                color: colors.greenAccent[400],
                px: 1,
                py: 0.5,
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                zIndex: 10,
                opacity: 0.9,
                boxShadow: 1,
                border: `1px solid ${colors.greenAccent[500]}`
              }}
            >
              Y-AXIS ZOOMED
            </Box>
          )}
          <ResponsiveLineCanvas
            data={filteredChartData}
            theme={chartTheme}
            margin={{ top: 20, right: 110, bottom: 60, left: 60 }}
            xScale={{
              type: 'point',
            }}
            yScale={{
              type: 'linear',
              min: yAxisRange.min,
              max: yAxisRange.max,
              stacked: false,
              reverse: false
            }}
            yFormat=" >-.1f"
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: 'Percentage (%)',
              legendOffset: -50,
              legendPosition: 'middle',
              format: v => `${v}%`
            }}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              legend: 'Date',
              legendOffset: 50,
              legendPosition: 'middle',
              format: value => {
                const date = new Date(value);
                const month = date.toLocaleString('default', { month: 'short' });
                const day = date.getDate();
                return `${month} ${day}`;
              }
            }}
            colors={d => d.color}
            lineWidth={3}
            pointSize={10}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            pointLabelYOffset={-12}
            // Aktiviere benutzerdefinierte Tooltips für einzelne Datenpunkte
            tooltip={({ point }) => {
              const formattedDate = new Date(point.data.x).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
              });
              
              // Stelle sicher, dass rawValue vorhanden ist und formatiere es
              const valueFormatted = formatNumber(point.data.rawValue);
              
              return (
                <Box
                  sx={{
                    padding: '12px 16px',
                    backgroundColor: colors.primary[400],
                    border: `1px solid ${point.serieColor}`,
                    borderRadius: '6px',
                    boxShadow: theme.shadows[3],
                    maxWidth: '250px',
                    zIndex: 9999
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: colors.grey[100] }}>
                    {formattedDate}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ color: point.serieColor, fontWeight: 'bold' }}>
                      {viewMode === 'distribution' ? 
                        (point.serieId === 'rewardPools' ? 'Reward Pools' : point.serieId.charAt(0).toUpperCase() + point.serieId.slice(1))
                        : point.serieId
                      }
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.grey[100] }}>
                      {formatPercent(point.data.y)} ({valueFormatted})
                    </Typography>
                  </Box>
                </Box>
              );
            }}
            enableArea={false}
            useMesh={true}
            // Fix enableSlices prop - change from true to "x"
            enableSlices="x"
            sliceTooltip={({ slice }) => (
              <Box
                sx={{
                  padding: '12px 16px',
                  backgroundColor: colors.primary[400],
                  border: `1px solid ${colors.grey[100]}`,
                  borderRadius: '6px',
                  boxShadow: theme.shadows[3],
                  maxWidth: '300px',
                  zIndex: 9999
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: colors.grey[100], mb: 1 }}>
                  {slice.points[0].data.x}
                </Typography>
                {slice.points.map(point => {
                  // Berechnen des abgekürzten absoluten Werts
                  const absSpsValue = formatNumber(point.data.rawValue);
                  
                  return (
                    <Box key={point.id} sx={{ display: 'flex', alignItems: 'center', mb: 0.8 }}>
                      <Box 
                        sx={{ 
                          width: 12, 
                          height: 12, 
                          borderRadius: '50%', 
                          backgroundColor: point.serieColor, 
                          mr: 1.5
                        }} 
                      />
                      <Typography variant="body2" sx={{ color: colors.grey[100] }}>
                        <strong>
                          {viewMode === 'distribution' 
                            ? (point.serieId === 'rewardPools' ? 'Reward Pools' : point.serieId.charAt(0).toUpperCase() + point.serieId.slice(1))
                            : point.serieId
                          }
                        </strong>: {formatPercent(point.data.y)} ({absSpsValue})
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
            enableGridX={false}
            enableGridY={true}
            // Optimierte dynamische Berechnung der Grid-Linien basierend auf yAxisRange
            gridYValues={(() => {
              // Bei leeren Daten standard Grid-Linien zurückgeben
              if (filteredChartData.length === 0) return [0, 5, 10, 15, 20, 25, 30];
              
              const { min, max, isZoomed } = yAxisRange;
              const range = max - min;
              
              // Bestimme die optimale Anzahl von Grid-Linien (5-8 Linien)
              const idealStep = range / 5;
              
              // Bestimme den Abstand basierend auf dem Bereich
              let step;
              if (isZoomed) {
                step = idealStep <= 0.5 ? 0.25 : 
                       idealStep <= 1 ? 0.5 : 
                       idealStep <= 2 ? 1 : 
                       idealStep <= 5 ? 2 :
                       idealStep <= 10 ? 5 : 10;
              } else {
                step = range <= 10 ? 1 : 
                       range <= 20 ? 2 : 
                       range <= 40 ? 5 : 10;
              }
              
              // Generiere Grid-Linien
              const values = [];
              for (let i = min; i <= max; i += step) {
                // Runde auf 2 Dezimalstellen um Gleitkomma-Ungenauigkeiten zu vermeiden
                values.push(Math.round(i * 100) / 100);
              }
              
              return values;
            })()}
            enablePoints={selectedTimeframe <= 30}
            legends={[
              {
                anchor: 'bottom-right',
                direction: 'column',
                justify: false,
                translateX: 100,
                translateY: 0,
                itemsSpacing: 2,
                itemDirection: 'left-to-right',
                itemWidth: 80,
                itemHeight: 20,
                itemOpacity: 0.85,
                symbolSize: 12,
                symbolShape: 'circle',
                symbolBorderColor: 'rgba(0, 0, 0, .5)',
                effects: [
                  {
                    on: 'hover',
                    style: {
                      itemBackground: 'rgba(0, 0, 0, .03)',
                      itemOpacity: 1
                    }
                  }
                ]
              }
            ]}
          />
        </Box>
      ) : (
        <Box 
          display="flex" 
          flexDirection="column" 
          justifyContent="center" 
          alignItems="center" 
          height="300px"
        >
          <HelpOutlineIcon sx={{ fontSize: '3rem', color: colors.grey[500] }} />
          <Typography variant="body1" color={colors.grey[300]}>
            No historical data available
          </Typography>
          <Typography variant="body2" color={colors.grey[500]} sx={{ mt: 1 }}>
            {viewMode === 'distribution' ? 'Distribution' : 'Reward pool'} data will appear here once collected
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SpsDistributionHistoryChart;